import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Microphone, Stop, Image as ImageIcon, Trash, CheckCircle, Camera } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import {
  INSPECTION_TYPE_MAP, INSPECTION_STATUT_MAP,
} from '../../constants/inspectionTypes';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * InspectionDetailSheet - Sprint 2
 * Vue detaillee d'une inspection. Permet d'ajouter notes vocales (transcription Whisper)
 * et photos. Affiche tout le contenu (notes, synthese, observations).
 */
export function InspectionDetailSheet({ open, onOpenChange, inspectionId, chantiers, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  // Photo upload state
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  const fetchInspection = useCallback(async () => {
    if (!inspectionId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/inspections/${inspectionId}`, { withCredentials: true });
      setData(res.data);
    } catch (_err) {
      toast.error('Erreur chargement inspection');
    } finally {
      setLoading(false);
    }
  }, [inspectionId]);

  useEffect(() => { if (open) fetchInspection(); }, [open, fetchInspection]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setIsTranscribing(true);
        try {
          const fd = new FormData();
          fd.append('file', blob, 'inspection_voice.webm');
          await axios.post(`${API}/inspections/${inspectionId}/voice`, fd, {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          toast.success('Note vocale ajoutee et transcrite');
          await fetchInspection();
          onChange?.();
        } catch (_err) {
          toast.error('Erreur transcription');
        } finally {
          setIsTranscribing(false);
        }
      };
      mr.start();
      setIsRecording(true);
    } catch (_err) {
      toast.error('Acces microphone refuse');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/inspections/${inspectionId}/photo`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Photo ajoutee');
      await fetchInspection();
      onChange?.();
    } catch (_err) {
      toast.error('Erreur upload photo');
    } finally {
      setIsUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteVoiceNote = async (noteId) => {
    try {
      await axios.delete(`${API}/inspections/${inspectionId}/voice/${noteId}`, { withCredentials: true });
      toast.success('Note vocale supprimee');
      await fetchInspection();
    } catch (_err) { toast.error('Erreur suppression'); }
  };

  const deletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API}/inspections/${inspectionId}/photo/${photoId}`, { withCredentials: true });
      toast.success('Photo supprimee');
      await fetchInspection();
    } catch (_err) { toast.error('Erreur suppression'); }
  };

  const chantierName = data ? (chantiers.find(c => c.id === data.chantier_id)?.name || '-') : '-';
  const typeInfo = data ? INSPECTION_TYPE_MAP[data.type] : null;
  const statutInfo = data ? INSPECTION_STATUT_MAP[data.statut] : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="inspection-detail-sheet">
        <SheetHeader>
          <SheetTitle data-testid="inspection-detail-title">
            {data?.titre || data?.type_label || 'Inspection'}
          </SheetTitle>
          <SheetDescription>
            {data?.date} - {chantierName}
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="py-10 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#FF6B00] mx-auto" />
          </div>
        )}

        {!loading && data && (
          <div className="mt-6 space-y-6">
            {/* Badges */}
            <div className="flex flex-wrap gap-2">
              {typeInfo && (
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${typeInfo.color}`}
                  data-testid="insp-detail-type">
                  {typeInfo.label}
                </span>
              )}
              {statutInfo && (
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statutInfo.color}`}
                  data-testid="insp-detail-statut">
                  {statutInfo.label}
                </span>
              )}
            </div>

            {/* Meta */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-gray-500 uppercase">Coordonnateur</p>
                <p className="font-medium">{data.coordonnateur || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Entreprises</p>
                <p className="font-medium">{(data.entreprises_concernees || []).join(', ') || '-'}</p>
              </div>
            </div>

            {/* Notes texte */}
            {data.notes_texte && (
              <section data-testid="insp-detail-notes">
                <h3 className="text-sm font-bold text-[#111827] mb-2 uppercase tracking-wide">Notes</h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{data.notes_texte}</p>
              </section>
            )}
            {data.synthese && (
              <section data-testid="insp-detail-synthese">
                <h3 className="text-sm font-bold text-[#111827] mb-2 uppercase tracking-wide">Synthese</h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{data.synthese}</p>
              </section>
            )}
            {data.observations && (
              <section data-testid="insp-detail-observations">
                <h3 className="text-sm font-bold text-[#111827] mb-2 uppercase tracking-wide">Observations</h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{data.observations}</p>
              </section>
            )}

            {/* Notes vocales */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-[#111827] uppercase tracking-wide">
                  Notes vocales ({(data.voice_notes || []).length})
                </h3>
                <Button type="button" size="sm"
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isTranscribing}
                  className={isRecording ? 'bg-red-500 hover:bg-red-600' : 'bg-[#FF6B00] hover:bg-orange-600'}
                  data-testid="insp-voice-record">
                  {isTranscribing ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Transcription...</>
                  ) : isRecording ? (
                    <><Stop size={16} weight="fill" className="mr-2" />Arreter</>
                  ) : (
                    <><Microphone size={16} weight="fill" className="mr-2" />Enregistrer</>
                  )}
                </Button>
              </div>
              {isRecording && (
                <div className="flex items-center gap-2 text-red-500 text-sm mb-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                  Enregistrement en cours...
                </div>
              )}
              <div className="space-y-2">
                {(data.voice_notes || []).length === 0 && (
                  <p className="text-xs text-gray-400 italic">Aucune note vocale</p>
                )}
                {(data.voice_notes || []).map(v => (
                  <div key={v.id} className="p-3 bg-orange-50 border border-orange-200 rounded-lg"
                    data-testid={`insp-voice-${v.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle size={14} className="text-emerald-600" weight="fill" />
                        <span className="text-[10px] font-bold text-emerald-700 uppercase">Transcription</span>
                      </div>
                      <button onClick={() => deleteVoiceNote(v.id)}
                        className="text-red-400 hover:text-red-600"
                        data-testid={`insp-voice-delete-${v.id}`}>
                        <Trash size={14} />
                      </button>
                    </div>
                    <p className="text-sm text-gray-800">{v.transcription}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Photos */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-[#111827] uppercase tracking-wide">
                  Photos ({(data.photos || []).length})
                </h3>
                <Button type="button" size="sm" variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingPhoto}
                  data-testid="insp-photo-upload-btn">
                  {isUploadingPhoto ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Envoi...</>
                  ) : (
                    <><Camera size={16} className="mr-2" />Ajouter</>
                  )}
                </Button>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
                  onChange={handlePhotoSelect}
                  data-testid="insp-photo-input" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                {(data.photos || []).length === 0 && (
                  <p className="text-xs text-gray-400 italic col-span-3">Aucune photo</p>
                )}
                {(data.photos || []).map(p => (
                  <div key={p.id} className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group"
                    data-testid={`insp-photo-${p.id}`}>
                    <img
                      src={`${API}/files/${p.id}/download`}
                      alt={p.original_filename}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <button onClick={() => deletePhoto(p.id)}
                        className="p-2 bg-white/90 rounded-full text-red-500"
                        data-testid={`insp-photo-delete-${p.id}`}>
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
